import React, {createContext, useContext} from "react";
import {Linking, Platform, Text} from "react-native";
import type {StyleProp, TextStyle} from "react-native";
import {useNavigation} from "@react-navigation/native";
import {NativeStackNavigationProp} from "@react-navigation/native-stack";
import MainStyle from "../../../MainStyle";
import Div from "../../lib/Div";

type TermsTextContextValue = {
  navigation: NativeStackNavigationProp<any>, openLink: (url: string) => void
};

const TermsTextContext = createContext<TermsTextContextValue | null>(null);

export const TermsTextProvider = ({children}: { children: React.ReactNode }) => {
  const navigation: NativeStackNavigationProp<any> = useNavigation();

  const openLink = (url: string) => {
    if (Platform.OS === 'web') {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    void Linking.openURL(url);
  };

  return (
    <TermsTextContext.Provider value={{navigation, openLink}}>
      {children}
    </TermsTextContext.Provider>
  );
};

export const useTermsText = () => {
  const context = useContext(TermsTextContext);

  if (!context) throw new Error('useTermsText must be used inside TermsTextProvider');

  return context;
};

// Single body size for every paragraph and list item: the `primary`/`secondary`
// variants now resolve to the same scale so font size stays consistent across
// sections (some call sites pass variant="primary", most use the default).
// Headings step up to 16px; bullet markers match the body so they align.
const styles = {
  text_s: [MainStyle.c_white, MainStyle.Barlow500, MainStyle.f_s_14, MainStyle.m_b_15, MainStyle.l_h_20],
  small_link: [MainStyle.c_lightblue, MainStyle.Barlow700, MainStyle.t_d_l_u],
  secondary_s: [MainStyle.c_white, MainStyle.Barlow500, MainStyle.f_s_14, MainStyle.m_b_15, MainStyle.l_h_20],
  title_s: [
    MainStyle.c_white, MainStyle.Barlow700, MainStyle.f_s_16, MainStyle.l_h_22, MainStyle.m_b_21, MainStyle.t_t_u
  ],
  dot_s: [MainStyle.c_white, MainStyle.Barlow500, MainStyle.f_s_14, MainStyle.l_h_20, MainStyle.m_x_8]
};

type ChildrenProps = {children: React.ReactNode};
type LegalTextStyle = Array<StyleProp<TextStyle>>;
type TextProps = ChildrenProps & {extraStyle?: StyleProp<TextStyle>, noMargin?: boolean, variant?: 'primary' | 'secondary'};
type ListItemProps = TextProps & {marker?: string, mb?: number};
type GroupedListItemProps = ChildrenProps & {marker?: string, mb?: number};

const textStyleForVariant = (variant: TextProps['variant']): LegalTextStyle => (
  variant === 'primary' ? styles.text_s : styles.secondary_s
);

export const Bold = ({children}: ChildrenProps) => (
  <Text style={MainStyle.Barlow700}>{children}</Text>
);

export const Regular = ({children}: ChildrenProps) => (
  <Text style={MainStyle.Barlow400}>{children}</Text>
);

export const Underline = ({children}: ChildrenProps) => (
  <Text style={MainStyle.t_d_l_u}>{children}</Text>
);

export const InlineLink = ({children, onPress}: ChildrenProps & {onPress: () => void}) => (
  <Text style={styles.small_link} onPress={onPress} accessibilityRole="link">{children}</Text>
);

export const Title = ({children}: ChildrenProps) => (
  <Text style={styles.title_s}>{children}</Text>
);

export const Paragraph = ({children, extraStyle, noMargin = false, variant = 'secondary'}: TextProps) => {
  const baseTextStyle = textStyleForVariant(variant);
  const textStyle = [...baseTextStyle];

  if (extraStyle) textStyle.push(extraStyle);
  if (noMargin) textStyle.push(MainStyle.m_b_0);

  return (
    <Text style={textStyle}>
      {children}
    </Text>
  );
};

export const ListItem = (props: ListItemProps) => {
  const {children, extraStyle, marker = '\u2022', mb, noMargin = true, variant = 'secondary'} = props;
  const textStyle = textStyleForVariant(variant);
  const itemTextStyle = [...textStyle];

  if (extraStyle) itemTextStyle.push(extraStyle);
  if (noMargin) itemTextStyle.push(MainStyle.m_b_0);

  return (
    <Div row mb={mb}>
      <Text style={styles.dot_s}>{marker}</Text>
      <Text style={itemTextStyle}>{children}</Text>
    </Div>
  );
};

export const ListItemGroup = (props: GroupedListItemProps) => {
  const {children, marker = '\u2022', mb} = props;

  return (
    <Div row mb={mb}>
      <Text style={styles.dot_s}>{marker}</Text>
      <Div flex={1}>{children}</Div>
    </Div>
  );
};
